import React from 'react';

import { defineMessages } from 'react-intl-next';

import {
	ATLASSIAN_ACCOUNT_ID,
	type Snippet,
	type SnippetOwner,
	type UserSnippetOwner,
} from '../snippetTypes.ts';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { AtlassianIcon } from '@atlaskit/logo';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	card: {
		paddingLeft: token('space.050'),
		paddingRight: '0',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		width: '100%',
		height: '100px',
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	preview: {
		width: '120px',
		minWidth: '120px',
		height: '70px',
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		padding: token('space.100'),
		alignItems: 'center',
		backgroundColor: 'transparent',
	},
});

export const messages = defineMessages({
	snippetUses: {
		id: 'snippets.panel.item.uses',
		defaultMessage: 'Uses',
		description: 'Shows the number of uses for a specific Block Templates',
	},
});

export const SnippetPanelItem = ({
	snippet,
	onClick,
}: {
	snippet: Snippet;
	onClick: () => void;
}) => {
	return (
		<SnippetCardButton onClick={onClick}>
			<Inline alignInline="start" alignBlock="center" space="space.150">
				<SnippetPreview />
				<SnippetInfo snippet={snippet} />
			</Inline>
		</SnippetCardButton>
	);
};

const SnippetCardButton = ({
	children,
	onClick,
}: {
	onClick: () => void;
	children: React.ReactNode;
}) => {
	return (
		<Pressable xcss={styles.card} onClick={onClick}>
			{children}
		</Pressable>
	);
};

const SnippetPreview = () => {
	return (
		<Box xcss={styles.preview}>
			{/*eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx*/}
			<Text>Preview Coming Soon</Text>
		</Box>
	);
};

const SnippetInfo = ({ snippet }: { snippet: Snippet }) => {
	const shouldUseAtlassianIcon = snippet.createdBy.accountId.toLowerCase() === ATLASSIAN_ACCOUNT_ID;

	const CreatedByAvatar = ({ snippetOwner }: { snippetOwner: SnippetOwner }) => {
		return (
			<>
				{shouldUseAtlassianIcon ? (
					<AtlassianIcon appearance="brand" size="xsmall" />
				) : (
					<Avatar
						appearance="circle"
						size="xsmall"
						src={(snippetOwner as UserSnippetOwner).avatarUrl}
						name={snippetOwner.displayName}
					/>
				)}
			</>
		);
	};

	return (
		<Stack alignInline="start" alignBlock="center" space="space.075">
			<Heading size="xsmall">{snippet.name}</Heading>
			<Inline separator={'•'}>
				<Inline alignInline="center" alignBlock="center" space="space.050">
					<CreatedByAvatar snippetOwner={snippet.createdBy} />
					<Text color="color.text.subtlest" size="small" maxLines={1}>
						{snippet.createdBy.displayName}
					</Text>
				</Inline>
			</Inline>
		</Stack>
	);
};
